import React, { useState } from 'react';
import { normaliseAvoidTags } from '../utils/profileOptions';

/**
 * ScanIntakeModal — two quick questions between the scan and the results.
 *
 * Age and sensitivities are asked HERE, not in onboarding, because this is the
 * only point where they visibly do something: they're threaded into the same
 * analysis call, so the read the user is about to see is genuinely shaped by
 * them. (Previously these answers were collected up front and never reached the
 * analysis at all, which is why the read felt generic.)
 *
 * Both are skippable — a blank intake leaves the prompt unchanged.
 */

const SANS = '"DM Sans", system-ui, sans-serif';

const AGE_OPTIONS = ['Under 18', '18–24', '25–34', '35–44', '45–54', '55+'];

// Short list only — the long tail is handled by the free-text field. These are
// the reactions that most change how a read should be phrased.
const QUICK_SENSITIVITIES = ['Fragrance', 'Alcohol (denat.)', 'Retinol', 'Essential oils', 'AHAs (glycolic, lactic)'];

export default function ScanIntakeModal({ isOpen, onSubmit, onSkip }) {
  const [age, setAge] = useState('');
  const [tags, setTags] = useState([]);
  const [other, setOther] = useState('');

  if (!isOpen) return null;

  const toggleTag = (t) => {
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const build = () => {
    const draft = other.trim();
    return {
      age_range: age || null,
      sensitivities: normaliseAvoidTags(draft ? [...tags, draft] : tags),
    };
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        fontFamily: SANS,
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '24px 24px 0 0',
          width: '100%',
          maxWidth: 480,
          padding: '12px 24px 32px',
          maxHeight: '92vh',
          overflowY: 'auto',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D1C9C0', margin: '0 auto 20px' }} />

        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1A1A1A', margin: 0 }}>
          Before I show your read
        </h2>
        <p style={{ fontSize: 14, color: '#888', lineHeight: 1.5, margin: '8px 0 0' }}>
          These help personalise your recommendations.
        </p>

        <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(42,50,75,0.45)', margin: '24px 0 12px' }}>
          Your age range
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {AGE_OPTIONS.map((a) => {
            const on = age === a;
            return (
              <button
                key={a}
                type="button"
                onClick={() => setAge(on ? '' : a)}
                style={{
                  padding: '14px',
                  borderRadius: 14,
                  border: on ? '1px solid #341C1C' : '1px solid rgba(42,50,75,0.15)',
                  background: on ? '#341C1C' : '#FFFFFF',
                  color: on ? '#FFFFFF' : '#2A324B',
                  fontSize: 14,
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                {a}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '26px 0 12px' }}>
          <span style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(42,50,75,0.45)' }}>
            Anything your skin reacts to?
          </span>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(42,50,75,0.45)' }}>optional</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
          {QUICK_SENSITIVITIES.map((t) => {
            const on = tags.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => toggleTag(t)}
                style={{
                  padding: '11px 16px',
                  borderRadius: 999,
                  border: on ? '1px solid #E3C9A8' : '1px solid rgba(42,50,75,0.15)',
                  background: on ? 'rgba(243,213,181,0.35)' : '#FFFFFF',
                  color: on ? '#2A1212' : '#2A324B',
                  fontSize: 13.5,
                  fontWeight: on ? 600 : 500,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                {t}
              </button>
            );
          })}
        </div>
        <input
          value={other}
          onChange={(e) => setOther(e.target.value)}
          placeholder="Anything else?"
          style={{
            width: '100%',
            background: '#FFFFFF',
            border: '1px solid rgba(42,50,75,0.15)',
            borderRadius: 12,
            padding: '14px 16px',
            fontSize: 14,
            color: '#2A324B',
            outline: 'none',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
            marginTop: 12,
          }}
        />

        <button
          type="button"
          onClick={() => onSubmit(build())}
          style={{
            width: '100%',
            padding: '16px',
            border: 'none',
            borderRadius: 100,
            background: '#341C1C',
            color: 'white',
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
            marginTop: 24,
          }}
        >
          See my results
        </button>
        <button
          type="button"
          onClick={onSkip}
          style={{
            width: '100%',
            padding: '12px',
            marginTop: 6,
            background: 'transparent',
            border: 'none',
            color: '#AFA99F',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Skip
        </button>
      </div>
    </div>
  );
}
